import { MeasurementError, UnitError, addDays, measurementType, prepareReading, type MeasurementErrorCode } from '@gk/contracts'

/** The daily weather reading, "Relevé du jour" (M21, the owner's answer of 2026-10-23: a rain gauge is in place, a thermometer will come, the greenkeeper types the
 *  figures by hand every day). It is the measurement notebook's own readings (M13), for the SITE (no area, no depth): the day's rainfall and, once there is a
 *  thermometer, the day's lowest and highest air temperature. ONE reading per type per club day: typing again on a day that has one is a correction that
 *  supersedes it (`measurement.correct@1`, the office's: the old value stays), never a second reading. A first reading is `measurement.record@1`, a field fact
 *  (offline-first, projected). Pure: no React, no clock (the club's day is given), no I/O. Tested in dailyWeather.test.ts. */

export const DAILY_TYPES = ['rainfall', 'air_temperature_min', 'air_temperature_max'] as const
export type DailyType = (typeof DAILY_TYPES)[number]
/** The fields of the form, one per type. */
export type DailyField = 'rain' | 'tmin' | 'tmax'
export const FIELD_TYPE: Record<DailyField, DailyType> = { rain: 'rainfall', tmin: 'air_temperature_min', tmax: 'air_temperature_max' }
export const DAILY_FIELDS: readonly DailyField[] = ['rain', 'tmin', 'tmax']
/** How many days the list shows, today included. */
export const DAILY_LIST_DAYS = 14

/** What a reading of the device's notebook must carry for this (the mirror's rows, with this device's unsent ones marked `_pending`). */
export interface DailyRow {
  id: string; measurement_type: string; area_id: string | null; depth_mm?: number | null; value: number
  observed_at: string; observed_local_date: string; entered_at: string; superseded_by_id?: string | null; _pending?: boolean
}
/** The site's value of one type on one day. */
export interface DayValue {
  id: string; value: number; pending: boolean
  /** Live readings of this type the site has that day. More than one only when two phones each took "the first" one before either was sent, or a file brought
   *  several: the list says so, and the newest typed is the one shown and the one a correction replaces. */
  count: number
}
export interface DailyDay { date: string; rain: DayValue | null; tmin: DayValue | null; tmax: DayValue | null }

const isDaily = (type: string): type is DailyType => (DAILY_TYPES as readonly string[]).includes(type)
const newestFirst = (a: DailyRow, b: DailyRow): number =>
  a.entered_at !== b.entered_at ? (a.entered_at < b.entered_at ? 1 : -1) : a.observed_at !== b.observed_at ? (a.observed_at < b.observed_at ? 1 : -1) : a.id < b.id ? 1 : a.id > b.id ? -1 : 0

/** The site's live readings of the three types, by `type|day`, newest typed first. A reading on an area (a rain gauge on a green) or at a depth is not the site's. */
export function siteReadings<R extends DailyRow>(rows: readonly R[]): Map<string, R[]> {
  const by = new Map<string, R[]>()
  for (const r of rows) {
    if (!isDaily(r.measurement_type) || r.area_id || (r.depth_mm !== null && r.depth_mm !== undefined) || r.superseded_by_id) continue
    const k = `${r.measurement_type}|${r.observed_local_date}`
    const list = by.get(k); if (list) list.push(r); else by.set(k, [r])
  }
  for (const list of by.values()) list.sort(newestFirst)
  return by
}
const valueOf = (list: readonly DailyRow[] | undefined): DayValue | null =>
  list && list.length ? { id: list[0]!.id, value: list[0]!.value, pending: !!list[0]!._pending, count: list.length } : null

/** One club day: the site's rainfall and the day's extremes, each `null` when nothing was read. */
export function dailyDay(by: Map<string, DailyRow[]>, date: string): DailyDay {
  return { date, rain: valueOf(by.get(`rainfall|${date}`)), tmin: valueOf(by.get(`air_temperature_min|${date}`)), tmax: valueOf(by.get(`air_temperature_max|${date}`)) }
}
/** The last `n` club days up to and including `today`, newest first; a day with nothing read is in the list too (it is shown as missing, never skipped). */
export function lastDays(by: Map<string, DailyRow[]>, today: string, n = DAILY_LIST_DAYS): DailyDay[] {
  return Array.from({ length: n }, (_, i) => dailyDay(by, addDays(today, -i)))
}
/** A day of the list, "jeu. 22/10" (with the year, "sam. 27/03/2021", for a day picked further back): a calendar day, formatted as one (never through the device's zone). */
export const dayLabel = (iso: string, lang: string, withYear = false): string =>
  new Intl.DateTimeFormat(lang.startsWith('en') ? 'en-GB' : 'fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit', ...(withYear ? { year: 'numeric' as const } : {}), timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`))
/** Nothing at all was read that day. */
export const isMissing = (d: DailyDay): boolean => !d.rain && !d.tmin && !d.tmax
/** The reminder on Today: the day's rainfall is not in yet. Shown the whole club day (the crew start at dawn and are gone by mid-afternoon: an evening threshold would
 *  remind nobody), quietly: a pill on the card, never a notification. */
export const reminderDue = (today: DailyDay): boolean => !today.rain

// ---- what a person typed ------------------------------------------------------------------------------------------------------------------
export type DailyProblem = MeasurementErrorCode | 'DAILY_RAIN_REQUIRED' | 'DAILY_DECIMALS' | 'DAILY_MIN_ABOVE_MAX' | 'DAILY_PENDING' | 'DAILY_CORRECT_DENIED' | 'DAILY_CORRECT_OFFLINE'
export type Typed = { ok: true; value: number } | { ok: false; code: DailyProblem }

/** A typed figure: spaces ignored, a comma or a point, a minus typed as "-" or "−" (a winter morning). The plausible range is the type's (`ref.measurement_type` through its
 *  twin `prepareReading`, the server's own checks), and no more decimals than the type is read to (rain to the tenth of a millimetre, temperatures to the tenth of a degree):
 *  a stricter rule than the server's four decimals, because a gauge or a thermometer does not read finer. Call it on a non-empty text. */
export function readTyped(type: DailyType, text: string): Typed {
  const def = measurementType(type)!
  const s = text.replace(/[\s  ]/g, '').replace(/[−–]/g, '-').replace(',', '.')
  const m = /^[+-]?(\d+)(?:\.(\d*))?$/.exec(s) ?? /^[+-]?()\.(\d+)$/.exec(s)
  if (!m) return { ok: false, code: 'MEASUREMENT_VALUE_INVALID' }
  if ((m[2] ?? '').length > def.decimals) return { ok: false, code: 'DAILY_DECIMALS' }
  try {
    prepareReading({ type, value: s, unit: def.unit, hasArea: false })
  } catch (e) {
    if (e instanceof MeasurementError || e instanceof UnitError) return { ok: false, code: e.code }
    throw e
  }
  return { ok: true, value: Number(s) }
}

// ---- the form: what saving it would send --------------------------------------------------------------------------------------------------
export type DailyForm = Record<DailyField, string>
/** The form as it opens on a day: what the day already holds, as `fmt` writes a number for this person ("2,5"); empty where nothing was read. */
export function prefill(day: DailyDay, fmt: (n: number) => string): DailyForm {
  const f = (v: DayValue | null) => (v ? fmt(v.value) : '')
  return { rain: f(day.rain), tmin: f(day.tmin), tmax: f(day.tmax) }
}
export type DailyAction =
  | { field: DailyField; type: DailyType; kind: 'record'; value: number }
  | { field: DailyField; type: DailyType; kind: 'correct'; value: number; correctsId: string }
export interface DailyPlan {
  /** Something to send and nothing wrong. */
  ok: boolean
  /** What is wrong, by field (the form shows it under the field). */
  problems: Partial<Record<DailyField, DailyProblem>>
  /** At most one command per type: a first reading, or a correction of the day's reading. A field left as it opened sends nothing. */
  actions: DailyAction[]
  /** Fields emptied although the day has a reading: a reading is never removed, so it stays as it is (a correction changes it). */
  kept: DailyField[]
}
/** What saving the form would do, by the rules above. `initial` is the form as it opened (`prefill`): a field left as it was is not judged again and sends nothing.
 *  The rainfall is required (0 when it did not rain); the temperatures are optional (no thermometer yet), and the lowest may not be above the highest (as typed, or as
 *  the day already holds them). A changed figure on a day that has a reading is a correction: only for a person who may correct (`measurement.manage`), online, and
 *  not while that reading is still waiting on this device to be sent. */
export function planDaily(form: DailyForm, initial: DailyForm, day: DailyDay, o: { mayCorrect: boolean; online: boolean }): DailyPlan {
  const problems: DailyPlan['problems'] = {}
  const actions: DailyAction[] = []
  const kept: DailyField[] = []
  const effective: Partial<Record<DailyField, number>> = {}
  const stored: Record<DailyField, DayValue | null> = { rain: day.rain, tmin: day.tmin, tmax: day.tmax }
  for (const field of DAILY_FIELDS) {
    const type = FIELD_TYPE[field]
    const text = form[field].trim()
    const has = stored[field]
    if (has) effective[field] = has.value
    if (text === '') {
      if (field === 'rain') problems.rain = 'DAILY_RAIN_REQUIRED'
      else if (has) kept.push(field)
      continue
    }
    if (text === initial[field].trim()) continue
    const typed = readTyped(type, text)
    if (!typed.ok) { problems[field] = typed.code; continue }
    effective[field] = typed.value
    if (!has) { actions.push({ field, type, kind: 'record', value: typed.value }); continue }
    if (typed.value === has.value) continue
    if (has.pending) problems[field] = 'DAILY_PENDING'
    else if (!o.mayCorrect) problems[field] = 'DAILY_CORRECT_DENIED'
    else if (!o.online) problems[field] = 'DAILY_CORRECT_OFFLINE'
    else actions.push({ field, type, kind: 'correct', value: typed.value, correctsId: has.id })
  }
  const touched = (f: DailyField) => form[f].trim() !== initial[f].trim()
  if (effective.tmin !== undefined && effective.tmax !== undefined && effective.tmin > effective.tmax && (touched('tmin') || touched('tmax')) && !problems.tmin && !problems.tmax) {
    problems[touched('tmax') ? 'tmax' : 'tmin'] = 'DAILY_MIN_ABOVE_MAX'
  }
  const wrong = Object.keys(problems).length > 0
  return { ok: !wrong && actions.length > 0, problems, actions: wrong ? [] : actions, kept }
}
